// Sound-word toasts: the katakana (or whatever script the trip's language uses) on a frosted pill
// with a small "reading · meaning" line (MAP_DIORAMA §4.4, WI-06). They show in a fixed band, never
// at the act (owner, 23 Oct: the words covered the acts): just under the top HUD, or just above the
// stop bar when the act is up there (sfxQueue toastBand), with the act's name on one line over the
// word (round 2: no "On the island" chip). Words come from the pack's sfx
// deck (a card id) or a place's beat (inline, docs/PLACES.md): nothing here knows the language.
// Each word holds ≥ 3 s and the next waits (map/sfxQueue). A tap opens a mini card with replay.
// The element stays mounted (no remount per word), so it is never the page's LCP candidate.
import { useEffect, useLayoutEffect, useRef } from 'react'
import { create } from 'zustand'
import type { Pack } from '../../shared/pack/schema'
import { say, scriptLang } from '../audio/tts'
import type { MotionTier } from '../motion/tier'
import { useStage } from '../stage/bridge'
import { t } from '../strings/en'
import { emptyQueue, placeToast, push, setOpen, tick, toastBand, wait, type SfxQueue, type SfxRef, type ToastBand } from './sfxQueue'

export type { InlineSfx } from './sfxQueue'

interface SfxState extends SfxQueue {
  /** An act is playing (a rare one's word waits for it to end). */
  actRunning: boolean
  show(card: SfxRef, at?: { x: number; y: number }, opts?: { untilActEnds?: boolean }): void
  /** Opens or closes the mini card. */
  setCard(open: boolean): void
  setAct(running: boolean): void
}

let timer: ReturnType<typeof setTimeout> | undefined
let ids = 0
const now = () => performance.now()

export const useSfx = create<SfxState>((set, get) => {
  /** Applies a queue change, moves it on if due, and sets the one timer for the next step. */
  const step = (q: SfxQueue) => {
    const at = now()
    const next = tick(q, at, get().actRunning)
    set({ shown: next.shown, since: next.since, waiting: next.waiting, open: next.open })
    clearTimeout(timer)
    const ms = wait(next, at, get().actRunning)
    if (ms !== null) timer = setTimeout(() => step(get()), Math.max(16, ms))
  }
  return {
    ...emptyQueue(),
    actRunning: false,
    show(card, at, opts) {
      step(push(get(), { id: ++ids, card, at: at ?? null, untilActEnds: !!opts?.untilActEnds }, now()))
    },
    setCard(open) {
      step(setOpen(get(), open, now()))
    },
    setAct(running) {
      if (running === get().actRunning) return
      set({ actRunning: running })
      step(get())
    },
  }
})

export function sfxCard(pack: Pack, cardId: string) {
  const card = pack.decks.find((d) => d.id === 'sfx')?.cards.find((c) => c.id === cardId)
  return card?.kind === 'reading' ? card : null
}

export function SfxToast({ pack, tier }: { pack: Pack; tier: MotionTier }) {
  const shown = useSfx((s) => s.shown)
  const open = useSfx((s) => s.open)
  const act = useStage((s) => s.act)
  const hud = useStage((s) => s.hud)
  const ref = useRef<HTMLDivElement>(null)
  const pill = useRef<HTMLButtonElement>(null)
  const ref0 = shown?.card
  const card = typeof ref0 === 'string' ? sfxCard(pack, ref0) : (ref0 ?? null)
  const rare = !!ref0 && typeof ref0 !== 'string' && !!ref0.rare
  const at = shown?.at ?? null

  useEffect(() => useSfx.getState().setAct(!!act), [act])
  // Leaving the Map clears the toast (it'd be stale when the Map comes back).
  useEffect(
    () => () => {
      clearTimeout(timer)
      useSfx.setState(emptyQueue())
    },
    [],
  )

  // A fixed band in the free strip, never at the act (sfxQueue toastBand): under the top HUD, or
  // above the stop bar. The band is picked once per word, so
  // opening its mini card never makes it jump. Positioned on the element directly, so a re-measure
  // never re-renders.
  // The act's name shares the band (owner, 23 Oct, round 2: no "On the island" chip over the
  // island): one line over the word, or on its own while the act plays between words, in the band
  // its last word used.
  const bandOf = useRef<{ id: number; band: ToastBand } | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || (!card && !act)) return
    const box = el.offsetParent?.getBoundingClientRect() ?? { left: 0, top: 0, width: innerWidth, height: innerHeight }
    const strip = { width: box.width, top: hud.top - box.top, bottom: (Number.isFinite(hud.bottom) ? hud.bottom : box.top + box.height) - box.top }
    if (shown && bandOf.current?.id !== shown.id) {
      bandOf.current = { id: shown.id, band: toastBand(el.offsetHeight, at ? { y: at.y - box.top } : null, strip) }
    }
    const band = bandOf.current?.band ?? 'top'
    const p = placeToast({ w: el.offsetWidth, h: el.offsetHeight }, band, strip)
    el.style.left = `${p.left}px`
    el.style.top = `${p.top}px`
    el.dataset.band = band
  }, [card, shown, at, open, hud.top, hud.bottom, act])
  // A new act starts in the top band unless a word moves it.
  const actId = act?.id
  useEffect(() => {
    if (!actId) bandOf.current = null
  }, [actId])

  // A new word pops (Full), fades in (Calm) or just appears (Still). Web Animations, not a
  // remount, so the element stays put.
  const id = shown?.id
  useEffect(() => {
    const el = pill.current
    if (!el || id === undefined || tier === 'still' || typeof el.animate !== 'function') return
    if (tier === 'calm') el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' })
    else el.animate([{ opacity: 0, transform: 'scale(.6)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 350, easing: 'cubic-bezier(.34,1.56,.64,1)' })
  }, [id, tier])

  const line = card ? t.map.sfx.line(card.reading, card.meaning) : ''
  return (
    <div ref={ref} className={`sfx-toast ${tier}${card ? ' show' : ''}${act ? ' has-act' : ''}${at ? ' in-world' : ''}${rare ? ' rare' : ''}${open ? ' open' : ''}`}>
      {act ? (
        // Its name, one line; a tap eases the camera to it (the ticker, for screen readers, says it).
        <button type="button" className={`act-chip press ${act.rarity}`} onClick={() => useStage.getState().set({ focusAct: useStage.getState().focusAct + 1 })}>
          <span>{act.hint ?? `${act.label} · ${act.variant}`}</span>
        </button>
      ) : null}
      <div className="sfx-word" role="status" aria-live="polite">
      {card ? (
        <>
          <button ref={pill} type="button" className="sfx-pill" aria-expanded={open} onClick={() => useSfx.getState().setCard(!open)}>
            <span className="kana" lang={scriptLang(card.script)}>
              {card.script}
            </span>
            <small>{open ? card.reading : line}</small>
          </button>
          {open ? (
            <div className="sfx-card">
              {card.meaning ? <p>{t.map.sfx.means(card.meaning)}</p> : null}
              <div className="row">
                <button type="button" className="btn sm y press" onClick={() => say(card.script)}>
                  {t.map.sfx.hear}
                </button>
                <button type="button" className="btn sm w press" onClick={() => useSfx.getState().setCard(false)}>
                  {t.map.sfx.close}
                </button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      </div>
    </div>
  )
}
